import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import DynamicTechBackground from "./DynamicTechBackground";

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.2 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[600px] md:min-h-screen bg-ax-dark overflow-hidden"
    >
      {/* Dynamic particle network background */}
      <DynamicTechBackground />

      {/* Subtle dark gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent z-[3]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70 z-[3]" />

      {/* HK Globe watermark accent */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 opacity-[0.03] z-[1]">
        <img
          src="https://readdy.ai/api/search-image?query=Minimalist%20wireframe%20globe%20outline%20centered%20on%20Asia%20Pacific%20region%2C%20Hong%20Kong%20highlighted%20as%20a%20glowing%20red%20dot%20node%2C%20dark%20background%20with%20thin%20silver%20latitude%20and%20longitude%20grid%20lines%2C%20abstract%20data%20network%20connections%20radiating%20from%20Hong%20Kong%20hub%2C%20futuristic%20clean%20line%20art%20style%2C%20monochrome%20silver%20and%20subtle%20red%20accent%2C%20corporate%20technology%20aesthetic%2C%20high%20resolution&width=800&height=800&seq=ax-globe-hk-1&orientation=squarish"
          alt=""
          className="w-[500px] md:w-[700px] lg:w-[900px] object-contain"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-center min-h-[600px] md:min-h-screen px-6 md:px-12 lg:px-20 pt-20">
        <div className="max-w-6xl">
          {/* Logo */}
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-100 mb-8">
            <div className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img
                src="https://storage.readdy-site.link/project_files/6a0c1208-1aa1-4dc1-a162-d37460a57aae/0b5070d0-85d1-4d21-beb7-9f575b36e41d_Gemini_Generated_Image_8x8ibm8x8ibm8x8i-1.png?v=d5f6abaf13dd8fd2a8405993a2764b9e"
                alt="AX Axis Logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Tag line */}
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-200 mb-8">
            <span className="inline-flex items-center gap-2 text-ax-silver-dark text-xs md:text-sm tracking-widest uppercase">
              <span className="w-8 h-[1px] bg-ax-red" />
              {t("hero.tagline")}
            </span>
          </div>

          {/* Main title */}
          <h1 className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-300 mb-6">
            <span className="block font-serif text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight">
              {t("hero.mainTitle")}
            </span>
            <span className="block font-display text-lg md:text-2xl lg:text-3xl text-ax-silver mt-3 font-light tracking-wide italic">
              {t("hero.subTitle")}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-500 text-ax-silver-dark text-sm md:text-base max-w-xl leading-relaxed mb-10">
            {t("hero.description")}
          </p>

          {/* CTA Button */}
          <div className="animate-in opacity-0 translate-y-6 transition-all duration-700 delay-700">
            <a
              href="#business"
              className="inline-flex items-center gap-3 bg-white text-ax-dark px-6 py-3 rounded-full text-sm font-medium hover:bg-ax-silver-light transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-6 h-6 bg-ax-red rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">AX</span>
              </span>
              {t("hero.cta")}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>

        {/* Brand identifier bottom right */}
        <div className="hidden md:block absolute bottom-8 right-6 md:right-12 lg:right-20 animate-in opacity-0 translate-y-6 transition-all duration-700 delay-1000">
          <p className="text-ax-silver-dark text-xs tracking-widest">
            {t("hero.brandIdentifier")}
          </p>
        </div>
      </div>
    </section>
  );
}